import React from "react";
import { Link } from "react-router-dom";
import { Check, Circle, ArrowRight, Star } from "lucide-react";

export function checklistSteps(listings, tier, profile) {
  const hasLogo = !!profile?.logo_url || listings.some((l) => !!l.logo_url);
  // Done as soon as a description exists — saved via the profile form or on any listing.
  const descDone = !!profile?.has_description || !!(profile?.description || "").trim() || listings.some((l) => (l.description || "").trim().length > 0);
  const upgraded = !!tier && tier !== "free";
  return [
    { key: "logo", label: "Add your company logo", hint: "Logos make your card stand out in category pages.", done: hasLogo },
    { key: "description", label: "Complete your description", hint: "A sentence or two (up to 30 words) — what you supply, who you serve, where you ship.", done: descDone },
    { key: "upgrade", label: "Upgrade to Founding Supplier", hint: "Receive matched RFQs and rank above every free listing.", done: upgraded },
  ];
}

// Shown on the dashboard for claimed free suppliers until every step is complete.
export default function ClaimedChecklist({ listings, tier, profile, onEditProfile }) {
  if (!listings || listings.length === 0) return null;
  const steps = checklistSteps(listings, tier, profile);
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;

  return (
    <div className="mb-4 bg-white border border-[#E8ECE9] p-5" data-testid="claimed-checklist">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="tracking-overline text-[10px] text-[#B35C4A]">Get the most from your listing</div>
          <div className="font-serif text-lg text-[#11211A] mt-0.5">{doneCount} of {steps.length} steps complete</div>
        </div>
        <div className="w-40 h-1.5 bg-[#E8ECE9]">
          <div className="h-full bg-[#264839] transition-all" style={{ width: `${(doneCount / steps.length) * 100}%` }} data-testid="claimed-checklist-progress" />
        </div>
      </div>
      <ol className="space-y-2">
        {steps.map((s) => {
          const action = s.key === "upgrade"
            ? <Link to="/pricing" className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 bg-[#B8860B] text-white hover:bg-[#9A7009] transition-colors inline-flex items-center gap-1" data-testid="checklist-action-upgrade"><Star className="w-3 h-3 fill-current" /> See plans</Link>
            : <button type="button" onClick={onEditProfile} className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#264839] text-[#264839] hover:bg-[#264839] hover:text-white transition-colors inline-flex items-center gap-1" data-testid={`checklist-action-${s.key}`}>Edit profile <ArrowRight className="w-3 h-3" /></button>;
          return (
            <li key={s.key} className={`flex items-start gap-3 p-3 ${s.done ? "bg-[#F0EFEA]/60" : "bg-[#FAFAF7] border border-dashed border-[#C4CFC9]"}`} data-testid={`checklist-step-${s.key}`} data-done={s.done ? "true" : "false"}>
              {s.done
                ? <span className="w-5 h-5 rounded-full bg-[#264839] text-white flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3 h-3" /></span>
                : <Circle className="w-5 h-5 text-[#C4CFC9] shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${s.done ? "text-[#7D8C85] line-through" : "text-[#11211A] font-medium"}`}>{s.label}</div>
                {!s.done && <div className="text-xs text-[#7D8C85] mt-0.5">{s.hint}</div>}
              </div>
              {!s.done && <div className="shrink-0">{action}</div>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
